import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionData, KybSubmissionDocument, DocumentType } from 'apis/legitimizer/types';
import { Typography as T } from 'common/typography';

import MissingFieldError from './MissingFieldError';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { useBankDetailsFieldsConfigMemo } from '../useFieldsConfigMemo';

type Props = {
  submissionDocuments: KybSubmissionDocument[];
  submissionData: KybSubmissionData;
  onEditData: (section: string) => void;
  setHasMissingFields?: (value: boolean) => void;
};

const Content = styled.div`
  text-align: start;
`;

const Legend = styled.legend`
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SecondaryAction = styled(Button).attrs({
  size: 'small',
  variant: 'secondary',
})``;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  & > li:not(:first-child) {
    margin-block-start: 16px;
  }
`;

const resolveDocuments = (documents: KybSubmissionDocument[], type: DocumentType) =>
  documents?.filter(document => document.documentType === type) || [];

const ReviewBankDetails = ({ submissionData, submissionDocuments, onEditData, setHasMissingFields }: Props) => {
  const sectionConfiguration = useContext(SectionConfigurationContext);
  const bankDetailsFieldsConfig = useBankDetailsFieldsConfigMemo({ sectionConfiguration, entityUUID: true });
  const intl = useIntl();

  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);
  const bankAccountDetails = submissionData.bankAccounts?.bankAccountDetails || [];

  const section = 'bank-details';
  const bankConfig = sectionConfiguration[section]?.bankAccountDetails ?? [];

  if (!sectionConfiguration.enabledSections.includes(section) || bankConfig.length === 0) {
    return null;
  }

  const documentNames = resolveDocuments(submissionDocuments, 'BANK_STATEMENT').map(
    document => document.originalFilename,
  );

  return (
    <Content>
      <Legend>
        <T.Heading6>
          <FormattedMessage id="compliance.kyb.sections.bank-details.title" />
        </T.Heading6>
        <SecondaryAction type="button" onClick={() => onEditData('bank-details')}>
          <FormattedMessage id="state.edit" />
        </SecondaryAction>
      </Legend>

      {bankAccountDetails.map(account => (
        <Content key={account.entityUUID}>
          {bankDetailsFieldsConfig.accountType.visible && (
            <FormRow label={t('compliance.kyb.sections.bank-details.account-type')}>
              {account.accountType?.value || (
                <MissingFieldError
                  shouldShow={bankDetailsFieldsConfig.accountType.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </FormRow>
          )}
          {bankDetailsFieldsConfig.accountOwner.visible && (
            <FormRow label={t('compliance.kyb.sections.bank-details.account-owner')}>
              {account.accountOwner?.value || (
                <MissingFieldError
                  shouldShow={bankDetailsFieldsConfig.accountOwner.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </FormRow>
          )}
          {bankDetailsFieldsConfig.bankIdentifier.visible && account.accountType?.value === 'IBAN' && (
            <FormRow label={t('compliance.kyb.sections.bank-details.bank-identifier')}>
              {account.bankId?.value || (
                <MissingFieldError
                  shouldShow={bankDetailsFieldsConfig.bankIdentifier.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </FormRow>
          )}
          {bankDetailsFieldsConfig.accountNumber.visible && (
            <FormRow label={t('compliance.kyb.sections.bank-details.account-number')}>
              {account.accountNumber?.value || (
                <MissingFieldError
                  shouldShow={bankDetailsFieldsConfig.accountNumber.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </FormRow>
          )}
          {bankDetailsFieldsConfig.payoutRecipients.visible && (
            <FormRow label={t('compliance.kyb.sections.bank-details.payout-report-emails')}>
              {account.payoutRecipients?.value?.join(', ') || (
                <MissingFieldError
                  shouldShow={bankDetailsFieldsConfig.payoutRecipients.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </FormRow>
          )}
          {bankDetailsFieldsConfig.bankDocuments.visible && (
            <FormRow label={t('compliance.kyb.sections.bank-details.proof-of-bank-account')}>
              {documentNames.length > 0 ? (
                <List>
                  {documentNames.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </List>
              ) : (
                <MissingFieldError
                  shouldShow={bankDetailsFieldsConfig.bankDocuments.required}
                  hasFieldErrorState={setHasMissingFields}
                />
              )}
            </FormRow>
          )}
        </Content>
      ))}
    </Content>
  );
};

export default ReviewBankDetails;
